import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cancellationsApi } from '../services/api';
import type { CreateCancellationInput } from '../types';

export function useCancellations(status?: string) {
  return useQuery({
    queryKey: ['cancellations', status],
    queryFn: async () => {
      const response = await cancellationsApi.getAll(status);
      return response.data;
    },
  });
}

export function useCancellation(id: number) {
  return useQuery({
    queryKey: ['cancellations', id],
    queryFn: async () => {
      const response = await cancellationsApi.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useSuggestions(cancellationId: number) {
  return useQuery({
    queryKey: ['suggestions', cancellationId],
    queryFn: async () => {
      const response = await cancellationsApi.getSuggestions(cancellationId);
      return response.data;
    },
    enabled: !!cancellationId,
  });
}

export function useCreateCancellation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCancellationInput) => cancellationsApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cancellations'] });
    },
  });
}

export function useUpdateCancellationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'pending' | 'resolved' | 'failed' }) =>
      cancellationsApi.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['cancellations'] });
      void queryClient.invalidateQueries({ queryKey: ['cancellations', id] });
    },
  });
}

export function useRegenerateSuggestions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cancellationId: number) => cancellationsApi.regenerateSuggestions(cancellationId),
    onSuccess: (_, cancellationId) => {
      void queryClient.invalidateQueries({ queryKey: ['suggestions', cancellationId] });
    },
  });
}

export function useConfirmSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cancellationId, suggestionId }: { cancellationId: number; suggestionId: number }) =>
      cancellationsApi.confirmSuggestion(cancellationId, suggestionId),
    onSuccess: (_, { cancellationId }) => {
      void queryClient.invalidateQueries({ queryKey: ['cancellations'] });
      void queryClient.invalidateQueries({ queryKey: ['cancellations', cancellationId] });
      void queryClient.invalidateQueries({ queryKey: ['suggestions', cancellationId] });
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
